import { useParams } from 'react-router'

import { TranslationComponent } from '@/components/translation'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import useTranslator from '@/hooks/translation'

import { useDashboardDelete } from './use-dashboard-delete'
import { useDashboardDeleteAlertDialog } from './use-dashboard-delete-alert-dialog'

type DashboardDeleteAlertDialogProps = {
  onDeleted: () => void
}

const DashboardDeleteAlertDialog = ({
  onDeleted,
}: DashboardDeleteAlertDialogProps) => {
  const { open, onOpenChange, data } = useDashboardDeleteAlertDialog()
  const { deleteDashboardAction, state } = useDashboardDelete()

  const { getTranslation } = useTranslator()

  const { botId } = useParams()
  const dashboard = data?.dashboard

  if (!open) {
    return null
  }

  if (!botId) {
    throw new Error('Bot ID do not found')
  }

  if (!dashboard) {
    throw new Error('Dashboard not found')
  }

  const handleOnSave = async () => {
    const success = await deleteDashboardAction(botId, dashboard.dashboard_id)
    if (success) {
      onOpenChange(false)
      onDeleted()
    }
  }

  const handleOnCancel = () => {
    onOpenChange(false)
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl">
            <TranslationComponent
              i18nKey="dashboards.delete.question"
              components={{
                bold: <strong className="font-bold" />,
              }}
              values={{ dashboardName: dashboard.name }}
            />
          </AlertDialogTitle>

          <AlertDialogDescription>
            {getTranslation('dashboards.delete.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={state.type === 'loading'}
            onClick={handleOnCancel}
          >
            {getTranslation('common.cancel')}
          </AlertDialogCancel>

          <AlertDialogAction
            disabled={state.type === 'loading'}
            onClick={handleOnSave}
          >
            {getTranslation('common.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export { DashboardDeleteAlertDialog }
